import { Button, Col, FormGroup, Label } from 'reactstrap';
import React, { FunctionComponent, useState } from 'react';
import { Field } from 'formik';
import { withApiData, ApiDataBinding } from 'react-api-data';
import { compose } from 'recompose';
import { withTranslation, WithTranslation } from 'react-i18next';

// components
import FormControl from 'components/utils/FormControl';
import { CenteredAlert } from 'components/styled';
import { ApiResponse, Location as DataCenter, ReactApiDataActions, SpeedAndInterfaceType } from 'constants/api';
import SpeedSelect from 'components/utils/SpeedSelect';
import CloseButton from 'components/utils/CloseButton';
import styled from 'styled-components';
import FormModal from './FormModal';
import RolesChecker from 'components/utils/RolesChecker';
import { Roles } from 'constants/literals';

interface InputProps {
    dataCenterObject: DataCenter;
    handleSync: () => void;
    exchangeUuid: string;
}

interface EnhanceProps {
    dataCenterRequest: ApiDataBinding<ApiResponse<DataCenter>>;
    apiDataActions: ReactApiDataActions;
    speedAndInterfaceTypes: ApiDataBinding<{ data: { speed_and_interface_types: SpeedAndInterfaceType[] } }>;
}

type Props = EnhanceProps & InputProps & WithTranslation;

type Destroyable<T> = T & {
    id?: number;
    frontend_id?: number;
    _destroy?: boolean;
};

const enhance = compose<Props, InputProps>(
    withApiData({
        dataCenterRequest: 'putDataCenter',
        speedAndInterfaceTypes: 'getSpeedAndInterfaceTypes',
    }),
    withTranslation()
);

type FormValues = {
    long_name: string;
    short_name: string;
    description: string;
    active: boolean;
    speed_and_interface_types: SpeedAndInterfaceType[];
    country: string;
    city: string;
    address1: string;
    contact_name: string;
    contact_email: string;
    phone_number: string;
};

const generateId = (() => {
    let id = 1;
    return () => {
        return id++;
    };
})();

const DataCenterAddModal: FunctionComponent<Props> = ({
    dataCenterRequest,
    apiDataActions,
    dataCenterObject,
    handleSync,
    exchangeUuid,
    speedAndInterfaceTypes,
    t,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [showNewSpeedSelect, setShowSpeedSelect] = useState(false);

    function toggle() {
        setIsOpen((isOpen) => !isOpen);
    }
    function onSubmit(values: FormValues) {
        const { speed_and_interface_types, ...params } = values;
        const speedIds = values.speed_and_interface_types.map((speed: SpeedAndInterfaceType) => speed.id.toString());
        return dataCenterRequest.perform(
            { locationUuid: dataCenterObject.uuid },
            { location: { ...params, speed_and_interface_type_ids: speedIds } }
        );
    }

    function onSuccess() {
        handleSync();
        apiDataActions.invalidateCache('getDataCenters');
        apiDataActions.invalidateCache('getExchange', { uuid: exchangeUuid });
        apiDataActions.perform('getExchange', { uuid: exchangeUuid });
    }

    const _getSpeedAndInterfaceTypes = (selectedSpeeds: Array<Destroyable<SpeedAndInterfaceType>>) => {
        const availableSpeeds = speedAndInterfaceTypes!.data!;
        const speedValuesFromDB = availableSpeeds
            ? Array.from(
                new Set(
                    availableSpeeds!
                        .data!.speed_and_interface_types!.map((item: SpeedAndInterfaceType) => item)
                        .sort((a: SpeedAndInterfaceType, b: SpeedAndInterfaceType) => (a.speed > b.speed ? 1 : -1))
                )
            )
            : [];

        const selectedSpeedsIds = selectedSpeeds.map((speed) => speed.id);
        return speedValuesFromDB.filter((speed: SpeedAndInterfaceType) => !selectedSpeedsIds.includes(speed.id));
    };

    const _removeResource = (resources: Destroyable<any>[], resource: Destroyable<any>) => {
        if (resource.id) {
            return resources.map((current) => {
                if (current.id === resource.id) {
                    return { ...current, _destroy: true };
                } else {
                    return current;
                }
            });
        } else if (resource.frontend_id) {
            return resources.filter((current) => current.frontend_id !== resource.frontend_id);
        } else {
            throw new Error('Cannot identify the resource');
        }
    };

    const _filterRemoved = (resources: Destroyable<any>[]) => resources.filter((resource) => !resource._destroy);

    return (
        <React.Fragment>
            <FormModal
                header={t('dataCenterModal.headerTitleEdit')}
                isOpen={isOpen}
                toggle={toggle}
                initialValues={
                    {
                        long_name: dataCenterObject.long_name,
                        short_name: dataCenterObject.short_name,
                        description: dataCenterObject.description,
                        active: dataCenterObject.active,
                        speed_and_interface_types: dataCenterObject.speed_and_interface_types,
                        country: dataCenterObject.country,
                        city: dataCenterObject.city,
                        address1: dataCenterObject.address1,
                        contact_name: dataCenterObject.contact_name,
                        contact_email: dataCenterObject.contact_email,
                        phone_number: dataCenterObject.phone_number,
                    } as FormValues
                }
                onSubmit={onSubmit}
                onSuccess={onSuccess}
                successMessage={t('dataCenterModal.editSuccessMessage')}
            >
                {({ errors, touched, values, setFieldValue }) => (
                    <React.Fragment>
                        {
                            <>
                                <div>
                                    <FormGroup row>
                                        <Col md={3}>
                                            <Label htmlFor="long_name">{t('dataCenterModal.longName')}</Label>
                                        </Col>
                                        <Col md={9}>
                                            <FormControl name="long_name" errors={errors} touched={touched} required />
                                        </Col>
                                    </FormGroup>
                                    <FormGroup row>
                                        <Col md={3}>
                                            <Label htmlFor="short_name">{t('dataCenterModal.shortName')}</Label>
                                        </Col>
                                        <Col md={9}>
                                            <FormControl name="short_name" errors={errors} touched={touched} required />
                                        </Col>
                                    </FormGroup>
                                    <FormGroup row>
                                        <Col md={3}>
                                            <Label htmlFor="description">{t('dataCenterModal.description')}</Label>
                                        </Col>
                                        <Col md={9}>
                                            <FormControl name="description" errors={errors} touched={touched} />
                                        </Col>
                                    </FormGroup>
                                    <FormGroup row>
                                        <Col md={3}>
                                            <Label htmlFor="active">{t('dataCenterModal.active')}</Label>
                                        </Col>
                                        <Col md={9}>
                                            <Field type="checkbox" name="active" checked={values.active} />
                                        </Col>
                                    </FormGroup>
                                    <FormGroup row>
                                        <Col md={3}>
                                            <Label htmlFor="speed_and_interface_types">
                                                {t('dataCenterModal.speeds')}
                                            </Label>
                                        </Col>
                                        <Col md={9}>
                                            {values.speed_and_interface_types.length > 0 && (
                                                <div className="d-flex flex-column mb-2">
                                                    {values.speed_and_interface_types.map((speed) => (
                                                        <StyledMap
                                                            key={speed.id}
                                                            className="d-flex align-items-center mb-2"
                                                        >
                                                            <div style={{ width: 120 }}>{speed.name}</div>
                                                            <CloseButton
                                                                onClick={() => {
                                                                    const newSpeeds = _removeResource(
                                                                        values.speed_and_interface_types,
                                                                        speed
                                                                    );
                                                                    setFieldValue(
                                                                        'speed_and_interface_types',
                                                                        _filterRemoved(newSpeeds)
                                                                    );
                                                                }}
                                                                className="ml-2"
                                                            />
                                                        </StyledMap>
                                                    ))}
                                                </div>
                                            )}
                                            {!showNewSpeedSelect && (
                                                <span
                                                    className="cursor-pointer text-primary"
                                                    onClick={() => setShowSpeedSelect(true)}
                                                >
                                                    <i className="fas fa-plus" /> {t('dataCenterModal.addSpeed')}
                                                </span>
                                            )}
                                            {showNewSpeedSelect && (
                                                <div className="d-flex align-items-center justfy-content-start">
                                                    <SpeedSelect
                                                        speedAndInterfaceTypes={_getSpeedAndInterfaceTypes(
                                                            values.speed_and_interface_types
                                                        )}
                                                        onChange={(speed) => {
                                                            const newSpeed = {
                                                                frontend_id: generateId(),
                                                                id: speed.id,
                                                                name: speed.name,
                                                                speed: speed.speed,
                                                            };
                                                            setFieldValue('speed_and_interface_types', [
                                                                ...values.speed_and_interface_types,
                                                                newSpeed,
                                                            ]);
                                                            setShowSpeedSelect(false);
                                                        }}
                                                        className="flex-grow-1"
                                                    />
                                                    <Button
                                                        color="primary"
                                                        onClick={() => setShowSpeedSelect(false)}
                                                        className="ml-2"
                                                    >
                                                        {t('modal.cancelBtn')}
                                                    </Button>
                                                </div>
                                            )}
                                        </Col>
                                    </FormGroup>
                                    <hr/>
                                    <FormGroup row>
                                        <Col md={3}>
                                            <Label htmlFor="country">{t('dataCenterModal.country')}</Label>
                                        </Col>
                                        <Col md={9}>
                                            <FormControl name="country" errors={errors} touched={touched} required />
                                        </Col>
                                    </FormGroup>
                                    <FormGroup row>
                                        <Col md={3}>
                                            <Label htmlFor="city">{t('dataCenterModal.city')}</Label>
                                        </Col>
                                        <Col md={9}>
                                            <FormControl name="city" errors={errors} touched={touched} required />
                                        </Col>
                                    </FormGroup>
                                    <FormGroup row>
                                        <Col md={3}>
                                            <Label htmlFor="address">{t('dataCenterModal.address')}</Label>
                                        </Col>
                                        <Col md={9}>
                                            <FormControl name="address1" errors={errors} touched={touched} required />
                                        </Col>
                                    </FormGroup>
                                    <hr/>
                                    <FormGroup row>
                                        <Col md={3}>
                                            <Label htmlFor="contact">{t('dataCenterModal.contactName')}</Label>
                                        </Col>
                                        <Col md={9}>
                                            <FormControl
                                                name="contact_name"
                                                errors={errors}
                                                touched={touched}
                                                required
                                            />
                                        </Col>
                                    </FormGroup>
                                    <FormGroup row>
                                        <Col md={3}>
                                            <Label htmlFor="email">{t('dataCenterModal.contactEmail')}</Label>
                                        </Col>
                                        <Col md={9}>
                                            <FormControl
                                                name="contact_email"
                                                errors={errors}
                                                touched={touched}
                                                required
                                            />
                                        </Col>
                                    </FormGroup>
                                    <FormGroup row>
                                        <Col md={3}>
                                            <Label htmlFor="phone">{t('dataCenterModal.phoneNumber')}</Label>
                                        </Col>
                                        <Col md={9}>
                                            <FormControl
                                                name="phone_number"
                                                errors={errors}
                                                touched={touched}
                                                required
                                            />
                                        </Col>
                                    </FormGroup>
                                    <CenteredAlert color="warning">
                                        {t('dataCenterModal.noteMandatoryFields')}
                                    </CenteredAlert>
                                </div>
                            </>
                        }
                    </React.Fragment>
                )}
            </FormModal>
            <RolesChecker roles={[Roles.admin, Roles.noc]}>
                <Button className="float-right" color="light" onClick={toggle}>
                    {t('dataCenterModal.editBtn')}
                </Button>
            </RolesChecker>
        </React.Fragment>
    );
};

const StyledMap = styled.div`
    font-size: 12px;
`;

export default enhance(DataCenterAddModal);
